// A drop onto another module (05g-1) — main.js's drop resolution + its actions: merge into
// a comparison (the shake → burst animation, 05g-2), switch the target's player, set Player
// Similarity's overlay, and a comparison's ✕ (unmerge). The rules are drop-rules.ts +
// merge-rules.ts (pure, tested); this file reads the two modules off the page and applies
// the result.
//
// A player change here calls the pick handler directly (05g's fork 2A) — main.js set the
// hidden `.player-select` and fired 'change', which ran the same handler. The select still
// holds the value (undo reads the player off it).
//
// Called by the move (useModuleMove.ts), drag-to-add (menu-drag.ts) and the comparison
// chips (Stats, Radar).

import { snapshotPlayersOrNone } from "../data/snapshot.js";
import { getPlayerShortName } from "../data/player-utils.js";
import { isSnapshotPlayer, playerById, type PlayerId } from "../data/players.js";
import { LIVE } from "../live/flag.js";
import { pickedOutsideSnapshot, pickedPlayer, pickedShortName } from "../live/picked-players.js";
import { ensurePlayerOption } from "./player-options.js";
import { knownCurrentSeason, knownKeeper, knownWomens } from "../live/player-gender.js";
import { playerIdFromSelect } from "./module-shell.js";
import { showInsteadOfWidget } from "../modules/bridge.js";
import { canvasApi } from "./shell-actions.js";
import { getModuleConfigSync } from "../modules/module-config.js";
import { FIRST_PAIR_COLORS, chargingHtml, chargingNames, nextCompareColor } from "./merge-rules.js";
import { getStickyPlayer } from "./sticky-player.js";
import { undoApi, type ModuleDropAction } from "./undo.js";
import { teamOfModule } from "../live/team.js";
import {
  groupJoiners as groupJoinersRule,
  isMergeCompatibleTitle as isMergeCompatibleTitleRule,
  isPlayerTokenTarget as isPlayerTokenTargetRule,
  isSimilarityOverlayTarget as isSimilarityOverlayTargetRule,
  isSwitchPlayerTarget as isSwitchPlayerTargetRule,
  resolveModuleDropAction as resolveRule,
  seasonFor as seasonForRule,
  singlePlayerOf,
  type DropModule,
  type DropWorld,
} from "./drop-rules.js";

/** The `_`-state these actions read / write on a module element. Players by id (ids-1). */
interface MergeState {
  _mergedPlayers?: PlayerId[];
  _mergedColors?: string[];
  _mergedSeasons?: (string | null | undefined)[];
  _selectedSeason?: string | null;
  _selectedSeasonPlayer?: PlayerId;
  _skipNextSaveState?: boolean;
  _similarityOverlayCandidate?: { id: PlayerId; season: string | null };
}
const stateOf = (mod: HTMLElement) => mod as unknown as MergeState;

function describe(mod: HTMLElement): DropModule {
  const s = stateOf(mod);
  const select = mod.querySelector<HTMLSelectElement>(".player-select");
  return {
    title: mod.querySelector(".module-title")?.textContent?.trim(),
    // "" stays "" — a picker with no player (see DropModule.selectValue).
    selectValue: select ? (select.value === "" ? "" : playerIdFromSelect(select.value)) : null,
    // `|| undefined`: only a set list makes it a comparison (main.js tested it for truth).
    mergedPlayers: s._mergedPlayers || undefined,
    mergedSeasons: s._mergedSeasons || undefined,
    selectedSeason: s._selectedSeason || undefined,
    selectedSeasonPlayer: s._selectedSeasonPlayer,
  };
}

function world(): DropWorld {
  return {
    config: (title) => getModuleConfigSync(title),
    players: snapshotPlayersOrNone(),
    sticky: getStickyPlayer(),
    extras: LIVE ? pickedOutsideSnapshot((id) => isSnapshotPlayer(snapshotPlayersOrNone(), id)) : [],
    // Live (T2-14): her gender as a live Stats / Radar tile or list row noted it; null = not
    // known yet. (It used to fall back to "a snapshot player is a woman"; live mode has none.)
    womensOf: LIVE ? knownWomens : undefined,
    keeperOf: LIVE ? knownKeeper : undefined,
    currentSeasonOf: LIVE ? knownCurrentSeason : undefined,
  };
}

/** The season a player shows on this module (main.js seasonFor). */
export function seasonFor(mod: HTMLElement, id: PlayerId): string | null | undefined {
  return seasonForRule(describe(mod), id, world());
}

/**
 * The season a player keeps on a comparison — what an unmerge puts back on the tile. Live: the
 * tile's own season pick (a real season id), else null = her current one. Not `seasonFor`: it
 * looks the season up in her SNAPSHOT record, which has no real season ids, so an unmerged live
 * tile came back on her snapshot season (McCabe 26/27 → 25/26; T2-16b).
 */
function keptSeason(mod: HTMLElement, id: PlayerId): string | null | undefined {
  if (!LIVE) return seasonFor(mod, id);
  const s = stateOf(mod);
  return s._selectedSeasonPlayer === id ? (s._selectedSeason ?? null) : null;
}

/** What dropping placed module `source` onto `target` does, or null (red). */
export function resolveModuleDropAction(source: HTMLElement, target: HTMLElement): ModuleDropAction | null {
  if (source === target) return null;
  return resolveRule(describe(source), describe(target), world());
}

/** A side-panel tile (only a title so far) merges into `target`. */
export function isMergeCompatibleTitle(title: string, target: HTMLElement | null): boolean {
  if (!target) return false;
  return isMergeCompatibleTitleRule(title, describe(target), world());
}

/** A bare player joins `target` (a Radar / Stats). `playerId` defaults to the sticky pick. */
export function isPlayerTokenTarget(
  target: HTMLElement | null,
  playerId?: PlayerId | null,
  season?: string | null,
): boolean {
  if (!target) return false;
  return isPlayerTokenTargetRule(describe(target), playerId ?? null, season, world());
}

export function isSwitchPlayerTarget(target: HTMLElement | null): boolean {
  return !!target && isSwitchPlayerTargetRule(describe(target), world());
}

/** A player becomes `target`'s overlay (a Similarity). `playerId` defaults to the sticky pick. */
export function isSimilarityOverlayTarget(target: HTMLElement | null, playerId?: PlayerId | null): boolean {
  return !!target && isSimilarityOverlayTargetRule(describe(target), playerId ?? null, world());
}

/** The source fades into the target, then goes (main.js's absorb). */
export function absorb(source: HTMLElement): void {
  source.classList.add("merge-absorbing");
  source.addEventListener(
    "animationend",
    () => {
      source.querySelectorAll("*").forEach((el) => {
        (el as unknown as { _resizeObserver?: ResizeObserver })._resizeObserver?.disconnect();
      });
      canvasApi().removeModule(source);
    },
    { once: true },
  );
}

/** A player's name on the ⚡ charging screen. */
function mergeShortName(id: PlayerId): string {
  const player = playerById(snapshotPlayersOrNone(), id);
  if (player) return getPlayerShortName(player);
  // Live: a Stats tile takes players the snapshot lacks (T2-14) — her name from her pick.
  if (LIVE) return pickedShortName(id);
  throw new Error(`mergeModules: player ${id} isn't in the snapshot`);
}

/**
 * Absorb `source` into `target` as one more player (main.js mergeModules, copied exactly).
 * A normal target becomes a 2-player comparison; a comparison takes a 3rd / 4th / 5th (the
 * drop rules cap it). The source is removed outright: nothing comes back onto the canvas.
 *
 * The animation: both shake together ("charging up") with a ⚡ screen in place of both
 * bodies, so the dragged module on top doesn't just keep showing its old content. Then the
 * target bursts as its content flips to the comparison, and the source shrinks away into it.
 */
export function mergeModules(source: HTMLElement, target: HTMLElement): void {
  const sourceSelect = source.querySelector<HTMLSelectElement>(".player-select");
  if (!sourceSelect) return;
  const sourceId = playerIdFromSelect(sourceSelect.value);
  // A merge needs a player on both sides — the drop rules only allow one between pickers
  // that have one.
  if (sourceId === null) throw new Error("mergeModules: the dragged module has no player");

  const t = stateOf(target);
  const isAddition = !!t._mergedPlayers;
  let targetId: PlayerId | null = null;
  if (!isAddition) {
    const targetSelect = target.querySelector<HTMLSelectElement>(".player-select");
    if (!targetSelect) return;
    targetId = playerIdFromSelect(targetSelect.value);
    if (targetId === null) throw new Error("mergeModules: the target has no player");
  }

  // BEFORE any change, so Undo brings both modules back. (A drop holds checkpoints: its
  // press already took the step.)
  undoApi().saveState();

  const html = chargingHtml(chargingNames(t._mergedPlayers ?? [targetId as PlayerId], sourceId, mergeShortName));
  for (const mod of [target, source]) {
    const body = mod.querySelector<HTMLElement>(".module-body");
    if (body) showInsteadOfWidget(body, html);
  }

  // Collide at one point: the dragged module sits right on the target, so the shake reads
  // as one impact, not two things vibrating apart. It also takes the target's exact size
  // (grows or shrinks, instantly — Ben, 2026-10-02), so the two line up edge to edge.
  source.style.left = target.style.left;
  source.style.top = target.style.top;
  source.style.width = target.style.width;
  source.style.height = target.style.height;
  source.style.zIndex = "51";

  source.classList.add("merge-shaking");
  target.classList.add("merge-shaking");

  // Any animation ending inside the target ends the shake — main.js's behaviour, kept.
  target.addEventListener(
    "animationend",
    () => {
      source.classList.remove("merge-shaking");
      target.classList.remove("merge-shaking");

      // Swap the content in as the burst starts, so the flash hides the pop-in. Each
      // player keeps the season their module showed (not their current one).
      if (isAddition) {
        const { _mergedPlayers: members, _mergedSeasons: seasons, _mergedColors: colors } = t;
        if (!members || !seasons || !colors) throw new Error("mergeModules: a comparison without its seasons / colours");
        members.push(sourceId);
        seasons.push(keptSeason(source, sourceId));
        colors.push(nextCompareColor(colors));
      } else {
        const firstId = targetId as PlayerId; // set above whenever this isn't an addition
        t._mergedPlayers = [firstId, sourceId];
        t._mergedColors = [...FIRST_PAIR_COLORS];
        t._mergedSeasons = [keptSeason(target, firstId), keptSeason(source, sourceId)];
      }
      canvasApi().refreshModuleBody(target);

      target.classList.add("merge-bursting");
      target.addEventListener("animationend", () => target.classList.remove("merge-bursting"), { once: true });

      absorb(source);
    },
    { once: true },
  );
}

/** A group member whose player joins (groupJoinersOnto). */
export interface GroupJoin {
  mod: HTMLElement;
  id: PlayerId;
}

/**
 * Who of a selected group (in this order) joins `target`, a Radar / Stats tile showing players
 * (Ben, 2026-11-02; drop-rules.ts groupJoiners). Empty for anything else. Players only: a target
 * showing a team takes none, and a member showing a team brings none.
 */
export function groupJoinersOnto(target: HTMLElement, sources: readonly HTMLElement[]): GroupJoin[] {
  if (teamOfModule(target) !== null) return [];
  const members = sources.filter((m) => m !== target && teamOfModule(m) === null);
  return groupJoinersRule(describe(target), members.map(describe), world()).map((j) => ({ mod: members[j.index]!, id: j.id }));
}

/**
 * The group's players join `target` all at once: ONE shake with every name on the ⚡ screen,
 * then one burst as the comparison shows them all. Unlike mergeModules the dragged modules
 * stay (the caller puts them back where they were). Each player keeps the season their module
 * showed. One Undo step (the drop's press took it).
 */
export function joinGroupPlayers(target: HTMLElement, joins: readonly GroupJoin[]): void {
  if (joins.length === 0) return;
  const t = stateOf(target);
  let firstId: PlayerId | null = null;
  if (!t._mergedPlayers) {
    const select = target.querySelector<HTMLSelectElement>(".player-select");
    firstId = select ? playerIdFromSelect(select.value) : null;
    if (firstId === null) throw new Error("joinGroupPlayers: the target has no player");
  }
  undoApi().saveState();

  const already = t._mergedPlayers ?? [firstId as PlayerId];
  const names = [...already, ...joins.map((j) => j.id)].map(mergeShortName);
  const body = target.querySelector<HTMLElement>(".module-body");
  if (body) showInsteadOfWidget(body, chargingHtml(names));
  target.classList.add("merge-shaking");
  target.addEventListener(
    "animationend",
    () => {
      target.classList.remove("merge-shaking");
      if (!t._mergedPlayers) {
        const first = firstId as PlayerId;
        t._mergedPlayers = [first];
        t._mergedColors = [FIRST_PAIR_COLORS[0]];
        t._mergedSeasons = [keptSeason(target, first)];
      }
      const { _mergedPlayers: members, _mergedSeasons: seasons, _mergedColors: colors } = t;
      if (!seasons || !colors) throw new Error("joinGroupPlayers: a comparison without its seasons / colours");
      for (const j of joins) {
        members.push(j.id);
        seasons.push(keptSeason(j.mod, j.id));
        colors.push(nextCompareColor(colors));
      }
      canvasApi().refreshModuleBody(target);
      target.classList.add("merge-bursting");
      target.addEventListener("animationend", () => target.classList.remove("merge-bursting"), { once: true });
    },
    { once: true },
  );
}

/** Show player `playerId` on `target` (a switch). One checkpoint first; the pick handler
 *  then takes none of its own. */
export function setModulePlayer(target: HTMLElement, playerId: PlayerId | null): void {
  const select = target.querySelector<HTMLSelectElement>(".player-select");
  if (!select || playerId === null) return;
  undoApi().saveState();
  // Live: a player the snapshot lacks (a Squad / Ranking row…) needs an option in this module's
  // picker first — without one the select silently stays empty and the module gets no player
  // (Ben, 2026-10-18: a Squad name dropped on a Shot Map showed "Could not load the data").
  const picked = LIVE ? pickedPlayer(playerId) : undefined;
  if (picked) ensurePlayerOption(select, picked);
  select.value = String(playerId);
  if (select.value !== String(playerId)) throw new Error(`Player ${playerId} isn't in this module's picker`);
  stateOf(target)._skipNextSaveState = true;
  canvasApi().onPlayerSelectChange(target, select);
}

/** A placed module dropped on a Shot Map / Pass Map / Passing Profile / Heat Map: the
 *  target takes its player, the source fades into it. */
export function switchModulePlayer(source: HTMLElement, target: HTMLElement): void {
  const select = source.querySelector<HTMLSelectElement>(".player-select");
  if (!select) return;
  setModulePlayer(target, playerIdFromSelect(select.value));
  absorb(source);
}

/** Similarity's overlay — never its base player (that's search-box only). */
export function setSimilarityOverlayCandidate(target: HTMLElement, playerId: PlayerId | null, season?: string | null): void {
  if (playerId === null) return;
  undoApi().saveState();
  stateOf(target)._similarityOverlayCandidate = { id: playerId, season: season || null };
  canvasApi().refreshModuleBody(target);
}

/** A placed module dropped on Similarity: its player (on its season) becomes the
 *  overlay, and the source fades into it. */
export function switchToSimilarityOverlay(source: HTMLElement, target: HTMLElement): void {
  const playerId = singlePlayerOf(describe(source));
  if (!playerId) return;
  // Live: the season the source tile shows (a real season id), else her current one — not
  // `seasonFor`, which reads her SNAPSHOT seasons (as an unmerge, `keptSeason`).
  setSimilarityOverlayCandidate(target, playerId, keptSeason(source, playerId));
  absorb(source);
}

/**
 * A comparison chip's ✕: drop that player (by index — two members can be the same player,
 * e.g. one player on two seasons). Nothing comes back onto the canvas. Down to one player, the
 * module is a normal single-player one again; otherwise the comparison re-renders. The
 * other players keep their colours (colour belongs to the player, not the position).
 */
export function unmergePlayer(mod: HTMLElement, idx: number): void {
  const s = stateOf(mod);
  if (!s._mergedPlayers) return;
  // BEFORE the change, so Undo brings the player back.
  undoApi().saveState();

  s._mergedPlayers.splice(idx, 1);
  if (s._mergedColors) s._mergedColors.splice(idx, 1);
  if (s._mergedSeasons) s._mergedSeasons.splice(idx, 1);

  if (s._mergedPlayers.length <= 1) {
    const remaining = s._mergedPlayers[0];
    const remainingSeason = s._mergedSeasons && s._mergedSeasons[0];
    delete s._mergedPlayers;
    delete s._mergedColors;
    delete s._mergedSeasons;
    const select = mod.querySelector<HTMLSelectElement>(".player-select");
    if (select && remaining) {
      // Set the season directly: Stats' own "new player → their current season" reset keys
      // off _selectedSeasonPlayer, which can already be `remaining` (one player compared
      // with their own other season) though the module just showed the removed season.
      s._selectedSeason = remainingSeason;
      s._selectedSeasonPlayer = remaining;
      // Live: the one left can be a pick the snapshot lacks (T2-14) — the picker needs her option.
      const picked = LIVE ? pickedPlayer(remaining) : undefined;
      if (picked) ensurePlayerOption(select, picked);
      select.value = String(remaining);
      s._skipNextSaveState = true;
      canvasApi().onPlayerSelectChange(mod, select);
    }
  } else {
    canvasApi().refreshModuleBody(mod);
  }
}
